import { useModal } from "@newsela/angelou";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

import { VIEW_PAGE_ASSIGN_GRAPHIC_ORGANIZERS_FEATURE } from "@/assignments/constants/experiments";
import useGetExportables from "@/assignments/useGetExportables";
import { GRAPHIC_ORGANIZER_PARAM } from "@/components/AIComponents/ExportablesModal";
import {
  TEMPLATE_REGISTRY,
  type TemplateDefinition,
} from "@/components/AIComponents/ExportablesModal/templates/registry";
import useHasFeature from "@/features/useHasFeature";
import { isSupportedEnglishContent } from "@/utils/generatedContent";
import contentTypes from "@/view/constants/contentTypes";
import { ExportablesContext } from "./useExportablesContext";

import type {
  ExportablesModalCommonProps,
  ExportablesContextValue,
} from "./ExportablesContextProvider.types";

interface ExportablesContextProps extends ExportablesModalCommonProps {
  children: React.ReactNode;
  isStudent: boolean;
}

const ExportablesContextProvider = ({
  children,
  content,
  contentMetadata = [],
  currentLevel,
  isStudent,
  levelId,
}: ExportablesContextProps) => {
  const contentId = content?.id || "";
  const contentType = content?.content_type ?? contentTypes.ARTICLE;

  const router = useRouter();
  const { pathname, query } = router;

  const [modalProps, modalSetActive, modalTriggerRef] = useModal(false);
  const [formData, setFormData] = useState<Record<string, Record<string, any>>>(
    {},
  );
  const [selectedExportable, setSelectedExportable] = useState(
    TEMPLATE_REGISTRY.AnticipationGuide,
  );
  // State to track which templates are selected for assignment
  const [templatesToAssign, setTemplatesToAssign] = useState<
    TemplateDefinition<any>[]
  >([]);
  // State to track assigned templates
  const [assignedTemplates, setAssignedTemplates] = useState<
    TemplateDefinition<any>[]
  >([]);

  const [showAssignmentWarning, setShowAssignmentWarning] = useState(false);
  const [
    isAssignableGraphicOrganizersActivityChecked,
    setIsAssignableGraphicOrganizersActivityChecked,
  ] = useState(true);

  // TODO: filter list of templates based on student assignments [LESS-4829]
  const templateList: TemplateDefinition<any>[] = isStudent
    ? (
        (query["studentGos"] ??
          "anticipation_guide,compare_contrast,know_wonder_learn") as string
      )
        .split(",")
        .map((go) =>
          (Object.values(TEMPLATE_REGISTRY) as TemplateDefinition<any>[]).find(
            (t) => t.seti_identifier === go,
          ),
        )
    : Object.values(TEMPLATE_REGISTRY);

  const isContentSupported = isSupportedEnglishContent({
    contentType,
    currentLevel,
    contentMetadata,
  });

  const [isInAssignGraphicOrganizersExperiment] = useHasFeature(
    VIEW_PAGE_ASSIGN_GRAPHIC_ORGANIZERS_FEATURE,
  );

  const { data: exportablesMetadata, loading: exportablesLoading } =
    useGetExportables({
      articleId: contentId,
      levelId,
      skip: !isContentSupported,
    }) || {};

  const isLoading = exportablesLoading || exportablesMetadata === null;

  const handleModalClose = () => {
    modalSetActive(false);

    const newQuery = { ...query };
    delete newQuery[GRAPHIC_ORGANIZER_PARAM];

    router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  const handleTemplateSelection = (template?: TemplateDefinition<any>) => {
    const templateToSelect = template || templateList[0];

    if (!templateToSelect) return;

    setSelectedExportable(templateToSelect);

    const newQuery = {
      ...query,
      [GRAPHIC_ORGANIZER_PARAM]: templateToSelect.seti_identifier,
    };

    router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  const initializedTemplates = useRef<Set<string>>(new Set());

  // Clear initializedTemplates when modal closes
  useEffect(() => {
    if (!modalProps?.isActive) {
      initializedTemplates.current.clear();

      // Only keep formData for assigned templates
      const preservedFormData: Record<string, Record<string, any>> = {};
      assignedTemplates.forEach((template) => {
        const templateFormData = formData[template.seti_identifier];
        if (templateFormData) {
          preservedFormData[template.seti_identifier] = templateFormData;
        }
      });

      setFormData(preservedFormData);
    }
  }, [modalProps?.isActive]);

  useEffect(() => {
    if (!selectedExportable || !exportablesMetadata || !modalProps?.isActive)
      return;

    const templateSetiIdentifier = selectedExportable.seti_identifier;

    // If we've already initialized this template, don't do it again
    if (initializedTemplates.current.has(templateSetiIdentifier)) {
      return;
    }

    // Check if this template is in assignedTemplates (has saved data)
    const isAssigned = assignedTemplates.some(
      (t) => t.seti_identifier === templateSetiIdentifier,
    );

    // If assigned and already has formData, keep it
    if (isAssigned && formData[templateSetiIdentifier]) {
      initializedTemplates.current.add(templateSetiIdentifier);
      return;
    }

    const templateData =
      exportablesMetadata?.[selectedExportable.seti_identifier] || {};

    setFormData((prevFormData) => ({
      ...prevFormData,
      [selectedExportable.seti_identifier]: {
        ...templateData,
        articleShortTitle: currentLevel?.short_title,
      },
    }));

    // Mark this template as initialized
    initializedTemplates.current.add(selectedExportable.seti_identifier);
  }, [
    currentLevel?.short_title,
    selectedExportable?.seti_identifier,
    JSON.stringify(exportablesMetadata),
    modalProps?.isActive,
    JSON.stringify(assignedTemplates),
    JSON.stringify(formData),
  ]);

  useEffect(() => {
    const graphicOrganizerParam = query[GRAPHIC_ORGANIZER_PARAM];

    if (graphicOrganizerParam && typeof graphicOrganizerParam === "string") {
      const template = templateList.find(
        (t) => t.seti_identifier === graphicOrganizerParam,
      );

      if (template) {
        setSelectedExportable(template);
        modalSetActive(true);
      }
    }
  }, [query, modalSetActive]);

  // Initialize templatesToAssign with assignedTemplates when modal opens
  useEffect(() => {
    if (modalProps?.isActive) {
      setTemplatesToAssign(assignedTemplates);
    }
  }, [modalProps.isActive, assignedTemplates]);

  const contextValue: ExportablesContextValue = {
    assignedTemplates,
    content,
    contentMetadata,
    currentLevel,
    formData,
    handleModalClose,
    handleTemplateSelection,
    isContentSupported,
    isInAssignGraphicOrganizersExperiment,
    isAssignableGraphicOrganizersActivityChecked,
    isLoading,
    levelId,
    modalProps,
    modalSetActive,
    modalTriggerRef,
    selectedExportable,
    setAssignedTemplates,
    setFormData,
    setIsAssignableGraphicOrganizersActivityChecked,
    setSelectedExportable,
    setShowAssignmentWarning,
    setTemplatesToAssign,
    showAssignmentWarning,
    templateList,
    templatesToAssign,
  };

  return (
    <ExportablesContext.Provider value={contextValue}>
      {children}
    </ExportablesContext.Provider>
  );
};

export default ExportablesContextProvider;
